/**
 * UTM Modal Custom Element
 *
 * Displays a modal when a matching UTM parameter is detected in the URL.
 * Uses native HTML <dialog> element for built-in modal functionality.
 * Persists the active campaign in sessionStorage until dismiss; shell templates may
 * re-fetch this section from the stored activation URL (Section Rendering API).
**/

const UTM_MODAL_STATE_KEY = 'utm_modal_state';
const UTM_MODAL_FETCH_SECTION_DEFAULT = 'utm-modal';

function normalizeFetchSectionId(id) {
  return id || UTM_MODAL_FETCH_SECTION_DEFAULT;
}

/**
 * @returns {{ active: object | null, dismissed: string[] }}
 */
function readState() {
  try {
    const raw = sessionStorage.getItem(UTM_MODAL_STATE_KEY);
    if (!raw) {
      return { active: null, dismissed: [] };
    }
    const data = JSON.parse(raw);
    if (!data || typeof data !== 'object') {
      return { active: null, dismissed: [] };
    }
    const active =
      data.active &&
      data.active.activationPath &&
      data.active.campaignHandle
        ? {
            activationPath: data.active.activationPath,
            campaignHandle: data.active.campaignHandle,
            fetchSectionId: normalizeFetchSectionId(data.active.fetchSectionId)
          }
        : null;
    const dismissed = Array.isArray(data.dismissed) ? data.dismissed.filter(Boolean) : [];
    return { active, dismissed };
  } catch (e) {
    return { active: null, dismissed: [] };
  }
}

function persistState(state) {
  const hasActive = !!(state.active && state.active.activationPath && state.active.campaignHandle);
  const dismissed = Array.isArray(state.dismissed) ? state.dismissed.filter(Boolean) : [];
  if (!hasActive && dismissed.length === 0) {
    sessionStorage.removeItem(UTM_MODAL_STATE_KEY);
    return;
  }
  sessionStorage.setItem(
    UTM_MODAL_STATE_KEY,
    JSON.stringify({
      active: hasActive ? state.active : null,
      dismissed: [...new Set(dismissed)]
    })
  );
}

function writeActiveSession(payload) {
  const { dismissed } = readState();
  persistState({ active: payload, dismissed });
}

/** Clears active campaign and records dismiss; removes storage key if nothing left to remember. */
function clearActiveAndRecordDismiss(handle) {
  if (!handle) {
    const { dismissed } = readState();
    persistState({ active: null, dismissed });
    return;
  }
  const { dismissed } = readState();
  const next = new Set(dismissed);
  next.add(handle);
  persistState({ active: null, dismissed: [...next] });
}

function buildSectionFetchUrl(activationPath, sectionId) {
  const rootRaw = window.Shopify?.routes?.root || '/';
  const root = rootRaw.replace(/\/+$/, '') || '';
  const path = activationPath.startsWith('/') ? activationPath : `/${activationPath}`;
  const params = new URLSearchParams({ sections: sectionId });
  return `${root}${path}?${params.toString()}`;
}

class UTMModal extends HTMLElement {
  constructor() {
    super();
    this.dialog = null;
  }

  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!this.dialog) {
      console.error('UTM Modal: No <dialog> element found');
      return;
    }

    // Close / CTA close / backdrop (delegation: works after fetch injects new nodes)
    this.dialog.addEventListener('click', (event) => {
      const t = event.target;
      const closer =
        t.closest &&
        (t.closest('[id^="ModalClose-"]') || t.closest('[id^="ModalCloseCTA-"]'));
      if (closer) {
        this.hide();
        return;
      }
      if (event.target === this.dialog) {
        this.hide();
      }
    });

    // Close on ESC key (native behavior, but we need to handle our cleanup)
    this.dialog.addEventListener('cancel', (event) => {
      event.preventDefault(); // Prevent default so we can run our hide() method
      this.hide();
    });

    void this.initUTM();
  }

  async initUTM() {
    const isShell = this.dataset.utmModalShell === 'true';
    const { active, dismissed } = readState();
    const dismissedSet = new Set(dismissed);

    if (isShell) {
      await this.initShell(active, dismissedSet);
      return;
    }

    await this.initEntry(active, dismissedSet);
  }

  async initEntry(active, dismissedSet) {
    const handle = this.dataset.modalHandle || '';
    const path = this.dataset.activationPath || window.location.pathname;
    const fetchSectionId = normalizeFetchSectionId(this.dataset.fetchSection);
    const debugMode = this.dataset.debugMode === 'true';

    if (!handle) {
      this.dispatchPriorityResolved('no-match');
      return;
    }

    if (active && active.campaignHandle && active.campaignHandle !== handle) {
      const ok = await this.fetchAndInject(active);
      if (ok) {
        this.triggerModal();
      } else {
        this.dispatchPriorityResolved('no-match');
      }
      return;
    }

    if (!debugMode && dismissedSet.has(handle)) {
      this.dispatchPriorityResolved('already-shown');
      return;
    }

    const urlMatch = this.hasMatchingUTMParameter();
    const pathMatch =
      active &&
      active.campaignHandle === handle &&
      active.activationPath === window.location.pathname;

    if (urlMatch) {
      writeActiveSession({
        activationPath: path,
        campaignHandle: handle,
        fetchSectionId
      });
      this.triggerModal();
      return;
    }

    if (pathMatch && active && !dismissedSet.has(handle)) {
      this.triggerModal();
      return;
    }

    this.dispatchPriorityResolved('no-match');
  }

  async initShell(active, dismissedSet) {
    const debugMode = this.dataset.debugMode === 'true';

    if (!active || !active.activationPath || !active.campaignHandle) {
      this.dispatchPriorityResolved('no-match');
      return;
    }

    if (!debugMode && dismissedSet.has(active.campaignHandle)) {
      this.dispatchPriorityResolved('already-shown');
      return;
    }

    const ok = await this.fetchAndInject(active);
    if (!ok) {
      this.dispatchPriorityResolved('no-match');
      return;
    }

    this.triggerModal();
  }

  async fetchAndInject(active) {
    const sectionId = normalizeFetchSectionId(active.fetchSectionId);
    const url = buildSectionFetchUrl(active.activationPath, sectionId);

    try {
      const response = await fetch(url);
      if (!response.ok) {
        return false;
      }
      const data = await response.json();
      const html = data[sectionId];
      if (!html || typeof html !== 'string') {
        return false;
      }

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const sectionWrapper = doc.querySelector('.shopify-section') || doc.body.firstElementChild;
      if (!sectionWrapper) {
        return false;
      }

      const fetchedHost = sectionWrapper.querySelector('utm-modal');
      if (!fetchedHost) {
        return false;
      }

      const fetchedDialog =
        fetchedHost.querySelector('dialog.utm-modal__dialog') || fetchedHost.querySelector('dialog');
      if (!fetchedDialog || !fetchedDialog.firstChild) {
        return false;
      }

      const ariaLabel = fetchedDialog.getAttribute('aria-label');
      if (ariaLabel) {
        this.dialog.setAttribute('aria-label', ariaLabel);
      }

      for (const attr of fetchedHost.attributes) {
        if (attr.name === 'id') continue;
        if (attr.name.startsWith('data-')) {
          this.setAttribute(attr.name, attr.value);
        }
      }

      this.dialog.innerHTML = '';
      while (fetchedDialog.firstChild) {
        this.dialog.appendChild(fetchedDialog.firstChild);
      }

      return true;
    } catch (err) {
      console.error('UTM Modal: Section fetch failed', err);
      return false;
    }
  }

  /**
   * Parse URL parameters into an object
   * @returns {Object} Key-value pairs of URL parameters
   */
  getURLParameters() {
    const params = {};
    const searchParams = new URLSearchParams(window.location.search);

    for (const [key, value] of searchParams.entries()) {
      params[key] = value.toLowerCase();
    }

    return params;
  }

  /**
   * Check if URL contains a matching UTM parameter
   * Dynamically checks all data-utm-* attributes on the modal element
   * @returns {boolean} True if there's a match
   */
  hasMatchingUTMParameter() {
    const urlParams = this.getURLParameters();
    const dataset = this.dataset;

    for (const key in dataset) {
      // Check if this is a UTM parameter (e.g., utmSource, utmMedium, utmCampaign)
      if (key.startsWith('utm')) {
        // Convert camelCase back to snake_case (utmSource -> utm_source)
        const paramName = key.replace(/([A-Z])/g, '_$1').toLowerCase();
        const configuredValues = dataset[key];

        if (!configuredValues) continue;

        // Get the URL parameter value
        const urlParamValue = urlParams[paramName];
        if (!urlParamValue) continue;

        // Split configured values and check for match
        const valuesToMatch = configuredValues
          .split(',')
          .map(val => val.trim().toLowerCase())
          .filter(val => val.length > 0);

        if (valuesToMatch.includes(urlParamValue)) {
          return true; // Found a match!
        }
      }
    }

    return false; // No matches found
  }

  /**
   * Show the modal using native showModal()
   */
  show() {
    if (!this.dialog) return;

    // Use native showModal() - handles focus trap, backdrop, ESC key automatically
    this.dialog.showModal();

    // Remove focus from auto-focused element (prevents outline on open)
    // Focus will return when user presses Tab (keyboard navigation)
    if (document.activeElement && document.activeElement.blur) {
      document.activeElement.blur();
    }

    // Apply simple scroll lock using overflow hidden
    // Native dialog doesn't block background scroll on all mobile browsers
    document.body.style.overflow = 'hidden';

    // Pause any playing media
    if (window.pauseAllMedia) {
      window.pauseAllMedia();
    }
  }

  /**
   * Hide the modal and clean up
   */
  hide() {
    if (!this.dialog) return;

    // Use native close() method
    this.dialog.close();

    // Remove scroll lock
    document.body.style.overflow = '';

    // Pause any playing media
    if (window.pauseAllMedia) {
      window.pauseAllMedia();
    }

    clearActiveAndRecordDismiss(this.dataset.modalHandle || '');

    // Dispatch custom event that other popups can listen for
    this.dispatchPriorityResolved('closed');
  }

  /**
   * Trigger the modal display and mark as shown
   */
  triggerModal() {
    try {
      this.show();
    } catch (error) {
      console.error('UTM Modal: Failed to show modal', error);
    }
  }

  /**
   * Dispatch priority-modal-resolved event
   * @param {string} reason - Reason for resolution ('closed', 'no-match', 'already-shown')
   */
  dispatchPriorityResolved(reason) {
    const event = new CustomEvent('priority-modal-resolved', {
      bubbles: true,
      detail: { modalId: this.id, reason }
    });
    document.dispatchEvent(event);
  }
}

// Define the custom element
if (!customElements.get('utm-modal')) {
  customElements.define('utm-modal', UTMModal);
}
